import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

interface PrivacySettings {
  profileVisibility: 'public' | 'patrons' | 'private';
  showEarnings: boolean;
  showPatronCount: boolean;
  showSupporters: boolean;
  allowMessages: 'everyone' | 'patrons' | 'none';
  showActivity: boolean;
  indexProfile: boolean;
  shareData: boolean;
}

const PrivacySettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState<PrivacySettings>({
    profileVisibility: 'public',
    showEarnings: false,
    showPatronCount: true,
    showSupporters: true,
    allowMessages: 'patrons',
    showActivity: true,
    indexProfile: true,
    shareData: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/privacy`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.settings) {
          setSettings(data.settings);
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/privacy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ settings }),
      });

      if (res.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
        >
          ← Back to Settings
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Privacy & Data</h1>
        <p className="mt-2 text-sm text-gray-600">
          Control who can see your information and how it's used
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Visibility */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <EyeIcon className="w-5 h-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">
              Profile Visibility
            </h2>
          </div>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.profileVisibility === 'public'}
                onChange={() =>
                  setSettings({ ...settings, profileVisibility: 'public' })
                }
                className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              />
              <span className="ml-3">
                <span className="block text-sm font-medium text-gray-700">
                  Public
                </span>
                <span className="block text-xs text-gray-500">
                  Anyone can view your profile
                </span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.profileVisibility === 'patrons'}
                onChange={() =>
                  setSettings({ ...settings, profileVisibility: 'patrons' })
                }
                className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              />
              <span className="ml-3">
                <span className="block text-sm font-medium text-gray-700">
                  Patrons Only
                </span>
                <span className="block text-xs text-gray-500">
                  Only your patrons can view your profile
                </span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.profileVisibility === 'private'}
                onChange={() =>
                  setSettings({ ...settings, profileVisibility: 'private' })
                }
                className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              />
              <span className="ml-3">
                <span className="block text-sm font-medium text-gray-700">
                  Private
                </span>
                <span className="block text-xs text-gray-500">
                  Only you can view your profile
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* What Others See */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            What Others Can See
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Show earnings
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Display your monthly earnings on your profile
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setSettings({
                    ...settings,
                    showEarnings: !settings.showEarnings,
                  })
                }
                className={`${
                  settings.showEarnings ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    settings.showEarnings ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Show patron count
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Display how many patrons you have
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setSettings({
                    ...settings,
                    showPatronCount: !settings.showPatronCount,
                  })
                }
                className={`${
                  settings.showPatronCount ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    settings.showPatronCount ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Show supporters list
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Display names of people who support you
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setSettings({
                    ...settings,
                    showSupporters: !settings.showSupporters,
                  })
                }
                className={`${
                  settings.showSupporters ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    settings.showSupporters ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Show activity status
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Let others see when you're online
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setSettings({
                    ...settings,
                    showActivity: !settings.showActivity,
                  })
                }
                className={`${
                  settings.showActivity ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    settings.showActivity ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Communication */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Who Can Message You
          </h2>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.allowMessages === 'everyone'}
                onChange={() =>
                  setSettings({ ...settings, allowMessages: 'everyone' })
                }
                className="h-4 w-4 text-indigo-600 border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">Everyone</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.allowMessages === 'patrons'}
                onChange={() =>
                  setSettings({ ...settings, allowMessages: 'patrons' })
                }
                className="h-4 w-4 text-indigo-600 border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">
                Patrons and creators I support
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.allowMessages === 'none'}
                onChange={() =>
                  setSettings({ ...settings, allowMessages: 'none' })
                }
                className="h-4 w-4 text-indigo-600 border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">No one</span>
            </label>
          </div>
        </div>

        {/* Search & Indexing */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <ShieldCheckIcon className="w-5 h-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">
              Search & Indexing
            </h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Allow search engines to index your profile
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Help people find you on Google and other search engines
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setSettings({
                    ...settings,
                    indexProfile: !settings.indexProfile,
                  })
                }
                className={`${
                  settings.indexProfile ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    settings.indexProfile ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Data Sharing */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Data Sharing
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700">
                Share anonymized data for analytics
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Help us improve the platform by sharing anonymized usage data
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setSettings({
                  ...settings,
                  shareData: !settings.shareData,
                })
              }
              className={`${
                settings.shareData ? 'bg-indigo-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
            >
              <span
                className={`${
                  settings.shareData ? 'translate-x-5' : 'translate-x-0'
                } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/dashboard/settings')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {success && (
          <div className="flex items-center justify-center p-4 bg-green-50 rounded-md">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm text-green-800">
              Privacy settings saved successfully!
            </span>
          </div>
        )}
      </form>
    </div>
  );
};

export default PrivacySettingsPage;
